import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { CustomValidators } from '@shared/customValidators';
import { Contact, CountryMetadata } from '@shared/models/data-model';
import { CountryService } from '@shared/services/country.service';

export interface BeneficialOwnerFormGroup {
    contactId: FormControl<string>;
    firstName: FormControl<string>;
    lastName: FormControl<string>;
    nationality: FormControl<string>;
    countryOfResidence: FormControl<string>;
    email: FormControl<string>;
}

@Component({
    selector: 'beneficial-owner-form',
    templateUrl: './beneficial-owner-form.component.html',
    styleUrls: ['./beneficial-owner-form.component.scss']
})
export class BeneficialOwnerFormComponent implements OnChanges {
    @ViewChild('firstNameInput') firstNameInput: ElementRef<HTMLInputElement>;

    @Input()
    boFormGroup: FormGroup<BeneficialOwnerFormGroup>;

    @Input()
    readonly: boolean = false;

    @Input()
    allCountries: CountryMetadata[];

    @Output()
    formInitialized = new EventEmitter<FormGroup>();

    defaultValidators: {[k in keyof BeneficialOwnerFormGroup]: ValidatorFn[]} = {
        contactId: [],
        firstName: [Validators.required, Validators.maxLength(CustomValidators.getContactMaxlength("firstName"))],
        lastName: [Validators.required, Validators.maxLength(CustomValidators.getContactMaxlength("lastName"))],
        nationality: [Validators.required],
        countryOfResidence: [Validators.required],
        email: [Validators.required, CustomValidators.emailValidatorFactory(), Validators.maxLength(CustomValidators.getContactMaxlength("email"))]
    };

    completeCountryList: CountryMetadata[];

    constructor(
        private readonly countryService: CountryService
    ) { }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.allCountries && this.allCountries) {
            this.initializeCountryList(this.allCountries);
        }
    }

    private initializeCountryList(allCountries: CountryMetadata[]) {
        this.completeCountryList = allCountries.filter(c => c.icc);
        this.completeCountryList.sort(this.countryService.sortFunction);
        if (this.readonly) {
            for (const control of Object.values(this.boFormGroup.controls)) {
                control.setValidators([]);
            }
        } else {
            for (const [key, control] of Object.entries(this.boFormGroup.controls)) {
                control.setValidators(this.defaultValidators[key]);
            }
        }
        this.formInitialized.emit(this.boFormGroup);
    }

    getNationalityLabel(): string {
        const nationality = this.boFormGroup.value.nationality;
        return this.completeCountryList?.find(c => c.iso3 === nationality)?.name ?? '';
    }

    getCountryOfResidenceLabel(): string {
        const countryOfResidence = this.boFormGroup.value.countryOfResidence;
        return this.completeCountryList?.find(c => c.iso3 === countryOfResidence)?.name ?? '';
    }

    static createBeneficialOwnerFormGroupForContact(fb: FormBuilder, contact: Contact): FormGroup<BeneficialOwnerFormGroup> {
        return fb.group({
            contactId: [contact.id],
            firstName: [contact.firstName],
            lastName: [contact.lastName],
            nationality: [contact.citizenshipCountryISO],
            countryOfResidence: [contact.address.countryISO],
            email: [contact.email]
        });
    }

    static createEmptyBeneficialOwnerFormGroup(fb: FormBuilder): FormGroup<BeneficialOwnerFormGroup> {
        return fb.group({
            contactId: [null],
            firstName: [null],
            lastName: [null],
            nationality: [null],
            countryOfResidence: [null],
            email: [null]
        });
    }
}
